// Layout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from './AlumniNavbar';
import Footer from './Footer';
import {motion,AnimatePresence} from "framer-motion"
import { ChevronUp, Loader2 } from 'lucide-react';

const Layout = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Longer loading time to show animation

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      setScrollProgress(progress);
      setIsVisible(scrollTop > 300);
      setIsScrolled(scrollTop > 64);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (isLoading) {
    return (
      <motion.div 
        className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 
          flex items-center justify-center overflow-hidden"
      >
        <div className="relative w-full max-w-lg mx-auto px-4">
          {/* Animated Background Elements */}
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full 
            mix-blend-multiply filter blur-xl opacity-70 animate-blob" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full 
            mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full 
            mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000" />

          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.5,
              ease: [0, 0.71, 0.2, 1.01],
              scale: {
                type: "spring",
                damping: 5,
                stiffness: 100,
                restDelta: 0.001
              }
            }}
            className="relative"
          >
            {/* Logo Container */}
            <div className="relative">
              <motion.img
                src="https://i.ibb.co/JB1mSr9/SAARTHI-removebg-preview.png"
                alt="Saarthi Logo"
                className="h-32 w-32 md:h-40 md:w-40 mx-auto"
                animate={{
                  scale: [1, 1.02, 1],
                  rotate: [0, 1, -1, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              {/* Loading Ring Animation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="w-48 h-48 md:w-56 md:h-56 rounded-full border-4 border-blue-500/30"
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: 360
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </div>

              {/* Inner Loading Ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  className="w-40 h-40 md:w-48 md:h-48 rounded-full border-4 border-indigo-500/40"
                  animate={{
                    scale: [1.1, 1, 1.1],
                    rotate: -360
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </div>
            </div>

            {/* Loading Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-8"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Loading Saarthi
              </h2>
              <p className="text-gray-600">Connecting alumni worldwide...</p>
            </motion.div>

            {/* Loading Progress Dots */}
            <div className="flex justify-center mt-4 space-x-2">
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full bg-blue-600"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-blue-50/30"
    >
      <Navbar />

      {/* Enhanced Progress Bar */}
      <motion.div 
        className="fixed top-20 left-0 right-0 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: scrollProgress > 0 ? 1 : 0 }}
      >
        <div className="h-1 bg-gradient-to-r from-blue-600 to-indigo-600 
          rounded-full shadow-lg transform origin-left transition-all duration-200 ease-out"
          style={{ width: `${scrollProgress}%` }}
        >
          <div className="absolute right-0 top-0 h-full w-4 bg-gradient-to-r 
            from-transparent to-white/20 rounded-full blur-sm" />
        </div>
      </motion.div>

      {/* Main Content with Page Transitions */}
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="flex-grow pt-32"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </motion.main>

      <Footer />

      {/* Enhanced Scroll to Top Button */}
      <AnimatePresence>
        {isVisible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 p-3 rounded-xl bg-white shadow-lg 
              hover:shadow-xl transition-all duration-300 group z-50"
          >
            <div className="relative">
              {/* Progress Circle */}
              <svg className="w-8 h-8 transform rotate-[-90deg]">
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  className="text-blue-600"
                  strokeDasharray={88}
                  strokeDashoffset={88 - (88 * scrollProgress) / 100}
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Arrow Icon */}
              <ChevronUp className="w-4 h-4 text-blue-600 absolute inset-0 m-auto
                group-hover:animate-bounce" />
            </div>
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Layout;
